import {
  dateToEpochSecondsOptional,
  epochSecondsToDateOptional,
  stripUndefined,
  z,
  type ZodType,
} from '@customerio/hydra-validation';
import type { ActionFromUI as UIAPIActionFromUI, ActionToUI as UIAPIActionToUI } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Action entity.
 *
 * `Action` is the generated ActionToUI wire shape with epoch-second
 * date fields swapped for `Date` instances. The generated schema models
 * the full detail response, but this cache layer also stores sparse rows
 * from campaign-list sideloads, so most fields intentionally remain
 * optional on the entity type.
 *
 * Mirrors the role of `services/db/collections/action.ts`: superset of
 * the sparse `ActionForCampaignsList` (campaigns sideload) and the full
 * `ActionToUI` (GET /actions/:id and mutations). Detail-only fields are
 * `undefined` on rows that arrived via sideload; mutations promote
 * summary→detail automatically since merged-into-cache writes the new
 * fields and leaves the rest alone.
 */

/* ------------------------------------------------------------------------- */
/* Type                                                                       */
/* ------------------------------------------------------------------------- */

/**
 * `subscription_topic_id` is normalized to `string` here even though the
 * wire ships it as `number | null` — mirrors the Tag/SubscriptionTopic
 * coercion convention so an action→subscription_topic FK follow can hit
 * the string-keyed `SubscriptionTopic` detail cache without per-call
 * `.toString()` plumbing. Same direction at write time.
 */
type ActionWire = Partial<Omit<UIAPIActionToUI, 'position' | 'type'>> & {
  id: string;
  position?: UIAPIActionToUI['position'] | null;
  type: string;
};

export type Action = Omit<ActionWire, 'created' | 'updated' | 'started' | 'subscription_topic_id'> & {
  created?: Date;
  updated?: Date;
  started?: Date;
  subscription_topic_id?: string | null;
};

type ActionWriteInput = Partial<Action>;
type ActionWireWritePayload = Partial<UIAPIActionFromUI>;

/* ------------------------------------------------------------------------- */
/* Schemas — only the transforms                                              */
/* ------------------------------------------------------------------------- */

const ActionFromApi = z.looseObject({
  created: epochSecondsToDateOptional,
  updated: epochSecondsToDateOptional,
  started: epochSecondsToDateOptional,
  subscription_topic_id: z
    .number()
    .nullish()
    .transform((n) => (n == null ? n : String(n))),
}) as unknown as ZodType<Action, unknown>;

export const ActionToApi = z
  .looseObject({
    created: dateToEpochSecondsOptional,
    updated: dateToEpochSecondsOptional,
    started: dateToEpochSecondsOptional,
    subscription_topic_id: z
      .string()
      .nullish()
      .transform((s) => (s == null ? s : Number(s)))
      .pipe(z.number().finite().nullish()),
  })
  .transform(stripUndefined) as unknown as ZodType<ActionWireWritePayload, ActionWriteInput>;

/* ------------------------------------------------------------------------- */
/* Entity                                                                     */
/* ------------------------------------------------------------------------- */

type ActionRelations = {
  readonly campaign: {
    readonly kind: 'belongs-to';
    readonly target: 'campaign';
    readonly via: 'campaign_id';
  };
};

/** @public */
export const action = defineEntity<Action, ActionWireWritePayload, void, ActionRelations, 'workspace'>({
  name: 'action',
  scope: 'workspace',
  pk: (a) => a.id,
  schema: { read: ActionFromApi, write: ActionToApi },
  relations: {
    campaign: { kind: 'belongs-to', target: 'campaign', via: 'campaign_id' },
  },
});

export const actionKeys = action.keys;

export function parseAction<T extends { id: string; type: string }>(raw: T): Action {
  return action.parse(raw);
}

/* ------------------------------------------------------------------------- */
/* Computed properties — selectors as plain functions                         */
/* ------------------------------------------------------------------------- */

const EMAIL_TYPES = new Set(['email_action', 'transactional_email', 'newsletter_email']);

/** @public */
export const isEmail = (a: Action) => EMAIL_TYPES.has(a.type);
/** @public */
export const isExit = (a: Action) => a.type === 'exit_action';
/** @public */
export const isDelay = (a: Action) =>
  a.type === 'time_delay_action' || a.type === 'time_window_action' || a.type === 'conditional_wait_action';
/** @public */
export const isSplitTest = (a: Action) => a.type === 'split_test_action';
/** @public */
export const isBranch = (a: Action) =>
  a.type === 'multi_conditional_branch_action' || a.type === 'true_false_branch_action';
/** @public */
export const isDraft = (a: Action) => a.action_state === 'draft';
